import { Component, signal, output, input, inject, OnInit, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { AddNewValue } from '../../../attributes/add-new-value/add-new-value';
import { AttributeForm } from './attribute-form/attribute-form';
import { AttributesTable } from './attributes-table/attributes-table';
import { AttributesService } from '../../../../../services/productAttributes/productAttributes.service';
import {
  ExtendedAttributeListItem,
  GlobalAttributeItemResponse,
  ProductAttributeItemResponse,
  ReorderItem,
  ExtendedAttribute,
} from '../../../../../services/productAttributes/productAttributes.model';
import { ConfirmDeleteModal } from './confirm-delete-modal/confirm-delete-modal';
import { ImportGlobalModal } from './import-global-model/import-global-model';
import { ErrorData } from '../../../../../utils/error-data/error-data';

@Component({
  selector: 'app-attributes-step',
  standalone: true,
  imports: [
    CommonModule,
    AddNewValue,
    AttributeForm,
    AttributesTable,
    ImportGlobalModal,
    ConfirmDeleteModal,
    ErrorData,
  ],
  templateUrl: './attributes-step.html',
  styleUrl: './attributes-step.scss',
})
export class AttributesStep implements OnInit {
  private attributesService = inject(AttributesService);
  productType = input<'create' | 'edit'>('create');

  productId = input.required<number>();
  initialAttributes = input<ProductAttributeItemResponse[]>([]);

  onComplete = output<ProductAttributeItemResponse[]>();
  onBack = output<void>();

  @ViewChild('valueModal') valueModal!: AddNewValue;

  loading = signal(false);
  attributesLoading = signal(false);
  error = signal<string | null>(null);
  isSaved = signal(false);
  hasPendingChanges = signal(false);
  maxSavedOrder = signal(0);

  attributesList = signal<ExtendedAttributeListItem[]>([]);
  globalAttributes = signal<GlobalAttributeItemResponse[]>([]);

  showValueModal = signal(false);
  showImportModal = signal(false);
  showMinAttrError = signal(false);

  currentAttributeIndex = signal<number | null>(null);
  currentValueIndex = signal<number | null>(null);
  editData = signal<{ en: string; ar: string | null } | null>(null);
  editAttrIndex = signal<number | null>(null);
  deleteConfig = signal<{ type: 'attr' | 'value'; attrIdx: number; valIdx?: number; name: string } | null>(null);

  isArabic = () => document.documentElement.dir === 'rtl';

  openEditAttr(i: number): void {
    this.editAttrIndex.set(i);
  }

  existingTitleEnList = computed(() => this.attributesList().map((a) => a.attributeKey.titleEn ?? ''));

  currentAttrValues = computed(() => {
    const idx = this.currentAttributeIndex();
    if (idx === null) return [];
    return (this.attributesList()[idx]?.attributeValues ?? []).map((v) => ({ en: v.titleEn ?? '', ar: v.titleAr }));
  });

  editAttrFormData = computed(() => {
    const idx = this.editAttrIndex();
    if (idx === null) return null;
    const attr = this.attributesList()[idx];
    return attr ? { titleEn: attr.attributeKey.titleEn ?? '', titleAr: attr.attributeKey.titleAr, index: idx } : null;
  });

  hasUnsavedChanges = computed(() => this.attributesList().some((attr) => attr.isNewlyAdded));

  canSaveNewChanges = computed(() => {
    const unsaved = this.attributesList().filter((a) => a.isNewlyAdded);
    if (unsaved.length === 0) return false;
    return unsaved.every((attr) => (attr.isGlobal ? !!attr.attributeKey.id : attr.attributeValues?.length > 0));
  });

  dataWithIndices = computed(() => {
    const allItems = this.attributesList();
    return allItems
      .filter((a) => a.isNewlyAdded)
      .map((attr) => ({
        attr: attr as ExtendedAttribute,
        index: allItems.indexOf(attr),
      }));
  });

  canProceed = computed(() => this.isSaved() && !this.hasUnsavedChanges() && this.attributesList().length > 0);

  ngOnInit(): void {
    this.loadInitialData();
  }

  private loadInitialData(): void {
    this.attributesLoading.set(true);
    forkJoin({
      globals: this.attributesService.getGlobalAttributes(),
      products: this.attributesService.getProductAttributes(this.productId()),
    }).subscribe({
      next: (res) => {
        this.globalAttributes.set(res.globals.list || []);
        this.processList(res.products.list || []);
        this.attributesLoading.set(false);
      },
      error: (err) => this.handleApiError(err),
    });
  }

  private processList(incomingList: ProductAttributeItemResponse[]): void {
    const processed = (incomingList || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item, index) => ({
        ...item,
        order: index + 1,
        isGlobal: item.type?.toLowerCase() === 'global' || (item.isGlobal ?? false),
        isNewlyAdded: false,
      }));
    this.attributesList.set(processed);
    this.maxSavedOrder.set(processed.length);
    if (processed.length > 0) this.isSaved.set(true);
    this.hasPendingChanges.set(false);
  }

  saveNewChanges(): void {
    if (this.loading()) return;

    const allItems = this.attributesList();
    const unsaved = allItems.filter((a) => a.isNewlyAdded);

    if (unsaved.length === 0) return;

    this.loading.set(true);
    this.error.set(null);

    const dataWithIndices = unsaved.map((attr) => {
      return {
        attr: attr as ExtendedAttribute,
        index: allItems.indexOf(attr),
      };
    });

    this.attributesService.saveProductAttributes(this.productId(), dataWithIndices).subscribe({
      next: () => {
        this.loading.set(false);

        this.loadInitialData();
      },
      error: (err) => {
        this.handleApiError(err);
        this.loading.set(false);
      },
    });
  }

  private handleReorderOnSave(): void {
    const list = this.attributesList();
    const reorderData: ReorderItem[] = list
      .map((attr, index) => ({
        productAttributeId: attr.id || attr.attributeKey.id!,
        order: index + 1,
      }))
      .filter((item) => item.productAttributeId);

    if (reorderData.length === 0) return;
    const toSave = this.dataWithIndices();
    if (toSave.length === 0) return;

    this.loading.set(true);
    this.error.set(null);

    this.attributesService.saveProductAttributes(this.productId(), toSave).subscribe({
      next: () => {
        this.loading.set(false);
        this.loadInitialData();
      },
      error: (err) => {
        this.handleApiError(err);
        this.loading.set(false);
      },
    });
  }

  addAttributeKey(key: { titleEn: string; titleAr: string | null }): void {
    const newAttr: ExtendedAttributeListItem = {
      attributeKey: { titleEn: key.titleEn.trim(), titleAr: key.titleAr?.trim() || null },
      attributeValues: [],
      isGlobal: false,
      isNewlyAdded: true,
    };
    this.attributesList.update((list) => [...list, newAttr]);
    this.isSaved.set(false);
    this.showMinAttrError.set(false);
  }

  saveEditedAttr(data: { titleEn: string; titleAr: string | null; index: number }): void {
    const attr = this.attributesList()[data.index];
    if (attr.attributeKey.id && !attr.isNewlyAdded) {
      this.attributesService
        .updateAttributeKey(this.productId(), attr.attributeKey.id, {
          titleEn: data.titleEn,
          titleAr: data.titleAr,
        } as any)
        .subscribe({
          next: () => this.loadInitialData(),
          error: (err) => this.handleApiError(err),
        });
    } else {
      this.attributesList.update((list) => {
        const updated = [...list];
        updated[data.index].attributeKey = {
          ...updated[data.index].attributeKey,
          titleEn: data.titleEn,
          titleAr: data.titleAr,
        };
        return updated;
      });
    }
    this.editAttrIndex.set(null);
  }

  handleSaveValue(data: { en: string; ar: string }): void {
    const aIdx = this.currentAttributeIndex();
    const vIdx = this.currentValueIndex();
    if (aIdx === null) return;
    const attr = this.attributesList()[aIdx];
    const val = { titleEn: data.en.trim(), titleAr: data.ar.trim() };

    if (attr.attributeKey.id && !attr.isNewlyAdded) {
      if (vIdx !== null && attr.attributeValues[vIdx].id) {
        this.attributesService
          .updateAttributeValue(this.productId(), attr.attributeKey.id, attr.attributeValues[vIdx].id!, val)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesService
          .createAttributeValues(this.productId(), attr.attributeKey.id, [val])
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      }
    } else {
      this.attributesList.update((list) => {
        const u = JSON.parse(JSON.stringify(list));
        if (vIdx !== null) u[aIdx].attributeValues[vIdx] = { ...val, id: 0 };
        else u[aIdx].attributeValues.push({ ...val, id: 0 });
        return u;
      });
    }
    this.closeModal();
  }

  confirmImport(attr: GlobalAttributeItemResponse): void {
    const newGlobal: ExtendedAttributeListItem = {
      attributeKey: {
        id: attr.attributeKey.id,
        titleEn: attr.attributeKey.titleEn,
        titleAr: attr.attributeKey.titleAr,
      },
      attributeValues: attr.attributeValues ? [...attr.attributeValues] : [],
      isGlobal: true,
      isNewlyAdded: true,
    };
    this.attributesList.update((list) => [...list, newGlobal]);
    this.showImportModal.set(false);
    this.isSaved.set(false);
    this.hasPendingChanges.set(true);
  }

  proceedToNext(): void {
    const list = this.attributesList();
    if (this.hasPendingChanges()) {
      this.loading.set(true);
      const reorderData: ReorderItem[] = list
        .filter((a) => a.id || a.attributeKey.id)
        .map((attr, index) => ({
          productAttributeId: attr.id || attr.attributeKey.id!,
          order: index + 1,
        }));
      this.attributesService.reorderAttributes(this.productId(), reorderData).subscribe({
        next: () => {
          this.loading.set(false);
          this.onComplete.emit(list as any);
        },
        error: (err) => this.handleApiError(err),
      });
    } else {
      this.onComplete.emit(list as any);
    }
  }

  handleReorder(newList: ExtendedAttributeListItem[]): void {
    this.attributesList.set(newList);
    this.hasPendingChanges.set(true);
  }

  openValueModal(i: number): void {
    if (this.attributesList()[i].isGlobal) return;
    this.currentAttributeIndex.set(i);
    this.currentValueIndex.set(null);
    this.editData.set(null);
    this.showValueModal.set(true);
    setTimeout(() => this.valueModal?.open(), 0);
  }

  editAttributeValue(ai: number, vi: number): void {
    if (this.attributesList()[ai].isGlobal) return;
    const val = this.attributesList()[ai].attributeValues[vi];
    this.currentAttributeIndex.set(ai);
    this.currentValueIndex.set(vi);
    this.editData.set({ en: val.titleEn ?? '', ar: val.titleAr ?? null });
    this.showValueModal.set(true);
    setTimeout(() => this.valueModal?.open(), 0);
  }

  requestDeleteValue(ai: number, vi: number): void {
    const val = this.attributesList()[ai].attributeValues[vi];
    this.deleteConfig.set({ type: 'value', attrIdx: ai, valIdx: vi, name: val.titleEn ?? '' });
  }

  requestDeleteAttr(i: number): void {
    this.deleteConfig.set({ type: 'attr', attrIdx: i, name: this.attributesList()[i].attributeKey.titleEn ?? '' });
  }

  confirmDelete(): void {
    const c = this.deleteConfig();
    if (!c) return;
    const attr = this.attributesList()[c.attrIdx];
    if (c.type === 'attr') {
      if (attr.attributeKey.id && !attr.isNewlyAdded) {
        this.attributesService
          .deleteAttributeKey(this.productId(), attr.attributeKey.id)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesList.update((l) => l.filter((_, i) => i !== c.attrIdx));
      }
    } else if (c.type === 'value' && c.valIdx !== undefined) {
      const v = attr.attributeValues[c.valIdx];
      if (attr.attributeKey.id && v.id && !attr.isNewlyAdded) {
        this.attributesService
          .deleteAttributeValue(this.productId(), attr.attributeKey.id, v.id)
          .subscribe({ next: () => this.loadInitialData(), error: (err) => this.handleApiError(err) });
      } else {
        this.attributesList.update((l) => {
          const u = [...l];
          u[c.attrIdx].attributeValues = u[c.attrIdx].attributeValues.filter((_, i) => i !== c.valIdx);
          return u;
        });
      }
    }
    this.deleteConfig.set(null);
  }

  private handleApiError(err: any): void {
    this.loading.set(false);
    this.attributesLoading.set(false);
    const msg =
      document.documentElement.dir === 'rtl'
        ? err.error?.messageAr || err.error?.message
        : err.error?.messageEn || err.error?.message;
    this.error.set(msg || 'Error occurred');
  }

  closeModal(): void {
    this.showValueModal.set(false);
    this.currentAttributeIndex.set(null);
    this.currentValueIndex.set(null);
  }

  skipStep(): void {
    this.onComplete.emit([]);
  }
}
